import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { lazy,Suspense } from 'react'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { api,type AxiosError,type ListEnvelope } from '@/lib/api-client'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useMutation,useQueryClient,type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTheme } from 'next-themes'
import { formatDistanceToNow } from 'date-fns'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Card,CardContent,CardDescription,CardHeader,CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
Plus,
MoreHorizontal,
Pencil,
Trash2,
X,
Mail,
Phone,
FileText,MapPin,
Building2,Clock,Search,
RefreshCw,
MoreVertical
} from 'lucide-react'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { queryKeys } from '@/lib/query-keys'
import { formatPhoneDisplay } from '@/lib/formatters'
import { extractArray } from '@/features/http'
import { useSelectedShopId } from '@/features/shared'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { formatInStoreTz } from '@/lib/time'

const AddEditSupplierDialog = lazy(() =>
  import('@/features/catalog/dialogs/add-edit-supplier-dialog').then((m) => ({
    default: m.AddEditSupplierDialog,
  }))
)

interface SupplierType {
  id: string
  name: string
}

interface LocationDetails {
  street: string
  city: string
  state: string
  zipCode: string
  country: string
  lat?: string
  long?: string
}

interface Supplier {
  id: string
  name: string
  slug: string
  supplierTypeId: string
  supplierType?: { name: string }
  license?: string
  ubi?: string
  locationDetails?: LocationDetails
  documentLinks?: string[]
  email?: string
  description?: string
  countryCode?: string
  phone?: string
  logo?: string
  createdAt?: string
  updatedAt?: string
}

type PaginatedData<T> = {
  data: T[]
  meta: {
    page: number
    totalPages: number
    totalItems: number
  }
}

type OptimisticPaginatedContext<T> = {
  previousData: [QueryKey, PaginatedData<T> | undefined][]
}

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(10),
  search: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/suppliers')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: SuppliersLayout,
})

let suppressedSuppliersAutoOpenSignature: string | null = null

function SuppliersLayout() {
  return <Outlet />
}

export function SuppliersPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery } = useSearch({ from: Route.id })
  const shopId = useSelectedShopId()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchQuery}`
  const queryClient = useQueryClient()
  const { resolvedTheme } = useTheme()

  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [supplierToDelete, setSupplierToDelete] = React.useState<Supplier | null>(null)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/suppliers/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

        navigate({ to: '/catalog/suppliers', search: (prev) => ({ ...prev }) })
    },
    [navigate]
  )

  const supplierTypesQuery = useQuery({
    queryKey: queryKeys.supplierTypes(),
    queryFn: async () => {
      const response = await api.get(`/supplier-types`)
      return extractArray<SupplierType>(response)
    },
  })
  const supplierTypes = supplierTypesQuery.data || []

  const suppliersQuery = useQuery({
    queryKey: queryKeys.suppliers(shopId, { page, limit, search: searchQuery }),
    queryFn: async () => {
      const response = await api.get<ListEnvelope>(`/suppliers`, {
        params: { page, limit, search: searchQuery || undefined },
      })
      const data = extractArray<Supplier>(response)
      // The API returns `meta.total`, not `totalItems` — normalize it so the
      // "of N" count and the optimistic add/delete updates read the real total.
      const meta = {
        page: response.meta?.page ?? page,
        totalPages: response.meta?.totalPages ?? 1,
        totalItems: response.meta?.total ?? response.meta?.totalItems ?? data.length,
      }
      return { data, meta }
    },
    enabled: !!shopId,
  })

  const selectedSupplierQuery = useQuery({
    queryKey: queryKeys.supplier(selectedId),
    queryFn: async () => {
      if (!selectedId) return null
      return api.get<Supplier>(`/suppliers/${selectedId}`)
    },
    enabled: !!selectedId,
  })

  const suppliers = suppliersQuery.data?.data || []
  const currentPageHasSelectedSupplier = !!selectedId && suppliers.some((supplier) => supplier.slug === selectedId || supplier.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedSupplier) return selectedId
    if (suppliersQuery.isLoading || suppliers.length === 0) return undefined
    if (!selectedId && suppressedSuppliersAutoOpenSignature === pageSignature) return undefined
    return suppliers[0].slug || suppliers[0].id
  })()
  const selectedSupplier = suppliers.find((s) => s.slug === effectiveSelectedId || s.id === effectiveSelectedId) || selectedSupplierQuery.data || null
  const totalItems = suppliersQuery.data?.meta?.totalItems || suppliers.length
  const totalPages = suppliersQuery.data?.meta?.totalPages || 1

  const closeSelectedSupplier = React.useCallback(() => {
    suppressedSuppliersAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/suppliers')) {
          suppressedSuppliersAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const saveSupplier = useMutation<void, unknown, Partial<Supplier> & { id?: string }, OptimisticPaginatedContext<Supplier>>({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...data } = payload
        await api.patch(`/suppliers/${id}`, data)
      } else {
        await api.post(`/suppliers`, payload)
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.suppliers(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<Supplier>>({ queryKey: queryKeys.suppliers(shopId) })

      queryClient.setQueriesData<PaginatedData<Supplier>>(
        { queryKey: queryKeys.suppliers(shopId) },
        (old) => {
          if (!old) return old

          if (payload.id) {
            return {
              ...old,
              data: old.data.map((item) =>
                item.id === payload.id ? { ...item, ...payload } : item
              ),
            }
          }

          const tempSupplier: Supplier = {
            id: `temp-${Date.now()}`,
            name: payload.name || '',
            slug: payload.name || '',
            supplierTypeId: payload.supplierTypeId || '',
            license: payload.license,
            ubi: payload.ubi,
            locationDetails: payload.locationDetails,
            documentLinks: payload.documentLinks,
            email: payload.email,
            description: payload.description,
            countryCode: payload.countryCode,
            phone: payload.phone,
            logo: payload.logo,
          }

          return {
            ...old,
            data: old.meta.page === 1 ? [tempSupplier, ...old.data] : old.data,
            meta: { ...old.meta, totalItems: old.meta.totalItems + 1 },
          }
        }
      )

      return { previousData }
    },
    onSuccess: () => {
      toast.success(`Supplier ${isEditing ? 'updated' : 'created'} successfully`)
      handleCloseDialog(false)
    },
    onError: (error: unknown, _payload, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      const msg = (error as AxiosError<{ message?: string }>).response?.data?.message
      toast.error(msg || 'Failed to save supplier')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers(shopId) })
    },
  })

  const deleteSupplier = useMutation<void, unknown, string, OptimisticPaginatedContext<Supplier>>({
    mutationFn: async (id: string) => {
      await api.delete(`/suppliers/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.suppliers(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<Supplier>>({ queryKey: queryKeys.suppliers(shopId) })

      queryClient.setQueriesData<PaginatedData<Supplier>>(
        { queryKey: queryKeys.suppliers(shopId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter((item) => item.id !== id),
            meta: { ...old.meta, totalItems: Math.max(0, old.meta.totalItems - 1) },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, deletedId) => {
      if (selectedId === deletedId) setSelectedId(null)
      toast.success('Supplier deleted successfully')
    },
    onError: (_error, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Failed to delete supplier')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers(shopId) })
    },
  })

  const handleCloseDialog = (open: boolean) => {
    setIsAddDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setIsEditing(false)
        setEditingId(null)
      }, 300)
    }
  }

  const handleEdit = (item: Supplier) => {
    setEditingId(item.id)
    setIsEditing(true)
    setIsAddDialogOpen(true)
  }

  const getTypeName = (typeId: string) => {
    return supplierTypes.find((t) => t.id === typeId)?.name || 'Unknown'
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Suppliers</h2>
          <p className="text-muted-foreground">Manage your suppliers and distributors.</p>
        </div>
        {(suppliers.length > 0 || suppliersQuery.isLoading) && (
          <Button
            onClick={() => {
              setIsEditing(false)
              setIsAddDialogOpen(true)
            }}
            className="transition-all hover:scale-105 hover:shadow-md"
          >
            <Plus className="mr-0.5 h-4 w-4" /> Add Supplier
          </Button>
        )}
      </div>

      {(suppliers.length > 0 || searchQuery || suppliersQuery.isLoading) && (
        <div className="flex items-center gap-2 max-w-sm w-full">
          <div className="relative flex-1 group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search suppliers..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void suppliersQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <Suspense fallback={null}>
        {isAddDialogOpen && (
          <AddEditSupplierDialog
            open={isAddDialogOpen}
            onClose={() => handleCloseDialog(false)}
            onSave={(data) => {
              const payload: Partial<Supplier> & { id?: string } = {
                name: data.name,
                supplierTypeId: data.typeId,
                license: data.license,
                ubi: data.ubi,
                email: data.email,
                // Persist bare digits (API convention) — the US mask is display-only.
                phone: (data.countryCode ?? '').trim() === '+1' ? (data.phone ?? '').replace(/\D/g, '') : data.phone,
                countryCode: data.countryCode,
                description: data.description,
                logo: data.logo,
                locationDetails: data.locationDetails,
                documentLinks: [],
              }
              if (isEditing && editingId) {
                payload.id = editingId
              }
              saveSupplier.mutate(payload)
            }}
            isPending={saveSupplier.isPending}
            isEditing={isEditing}
            initialData={
              isEditing && editingId && suppliers.find((s) => s.id === editingId)
                ? (() => {
                    const item = suppliers.find((s) => s.id === editingId)!
                    return {
                      name: item.name,
                      typeId: item.supplierTypeId,
                      license: item.license || '',
                      ubi: item.ubi || '',
                      email: item.email || '',
                      phone: item.phone || '',
                      countryCode: item.countryCode || '+1',
                      description: item.description || '',
                      logo: item.logo || '',
                      locationDetails: {
                        street: item.locationDetails?.street || '',
                        city: item.locationDetails?.city || '',
                        state: item.locationDetails?.state || '',
                        zipCode: item.locationDetails?.zipCode || '',
                        country: item.locationDetails?.country || 'USA',
                        lat: item.locationDetails?.lat || '',
                        long: item.locationDetails?.long || '',
                      },
                    }
                  })()
                : undefined
            }
            supplierTypes={supplierTypes}
          />
        )}
      </Suspense>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!suppliersQuery.isLoading && suppliers.length === 0 ? '' : 'rounded-md squircle border overflow-hidden'} ${selectedSupplier ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}>
          {!suppliersQuery.isLoading && suppliers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No suppliers found</p>
                <Button
                  onClick={() => {
                    setIsEditing(false)
                    setIsAddDialogOpen(true)
                  }}
                  className="w-fit"
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Supplier
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Supplier</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="hidden md:table-cell">Country</TableHead>
                    <TableHead className="w-[80px] text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliersQuery.isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-md" />
                            <Skeleton className="h-5 w-32" />
                          </div>
                        </TableCell>
                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                        <TableCell className="hidden md:table-cell"><Skeleton className="h-5 w-16" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    suppliers.map((item) => (
                      <TableRow
                        key={item.id}
                        className={`cursor-pointer transition-colors group ${effectiveSelectedId === item.id || effectiveSelectedId === item.slug ? 'bg-muted' : ''}`}
                        onClick={() => {
                          if (item.slug === effectiveSelectedId || item.id === effectiveSelectedId) {
                            closeSelectedSupplier()
                            return
                          }
                          setSelectedId(item.slug || item.id)
                        }}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1.5">
                            <div className="h-10 w-10 rounded-md border flex items-center justify-center overflow-hidden bg-muted/50">
                              <ImageWithSkeleton
                                src={item.logo ?? (resolvedTheme === 'dark' ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png' : '/placeholder-light/Place%20Holder%20Light%204_4.png')}
                                alt={item.name}
                                wrapperClassName="h-full w-full"
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div>
                              {item.name}
                              {item.email && <span className="block text-xs text-muted-foreground">{item.email}</span>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.supplierType?.name || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{item.locationDetails?.country || '-'}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              <DropdownMenuItem className="gap-1.5" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
                                <Pencil className="h-4 w-4" />Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={(e) => { e.stopPropagation(); setSupplierToDelete(item); }}>
                                <Trash2 className="h-4 w-4" />Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {(suppliers.length > 0 || suppliersQuery.isLoading) && (
              <DataTablePagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={limit}
                entityName="suppliers"
                isLoading={suppliersQuery.isLoading}
                onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
                onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
              />
              )}
            </>
          )}
        </div>

        {selectedSupplier && (
          <Card className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
              <div className="flex gap-1.5 items-start">
                 <div className="w-12 h-12 rounded-full border bg-muted/50 flex items-center justify-center shrink-0 overflow-hidden">
                  <ImageWithSkeleton
                    src={selectedSupplier.logo ?? (resolvedTheme === 'dark' ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png' : '/placeholder-light/Place%20Holder%20Light%204_4.png')}
                    alt={selectedSupplier.name}
                    wrapperClassName="h-full w-full"
                    className={`w-full h-full object-cover${!selectedSupplier.logo ? ' opacity-50' : ''}`}
                  />
                 </div>
                <div className="grid gap-1">
                  <CardTitle className="text-xl flex items-center gap-1.5 leading-none">{selectedSupplier.name}</CardTitle>
                  <CardDescription className="font-mono text-xs">{selectedSupplier.slug}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-1 -mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      onClick={() => handleEdit(selectedSupplier)}
                      className="gap-1.5 cursor-pointer"
                    >
                      <Pencil className="h-4 w-4" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setSupplierToDelete(selectedSupplier)}
                      className="gap-1.5 text-red-500 hover:text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="ghost" size="icon" onClick={closeSelectedSupplier} className="transition-transform duration-200">
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 overflow-y-auto pb-0 px-6">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="px-2 py-1">
                  {getTypeName(selectedSupplier.supplierTypeId)}
                </Badge>
              </div>

              <div className="space-y-4">
                <div className="space-y-3 p-3 bg-muted/40 rounded-lg">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1.5">
                    <Building2 className="w-3 h-3" /> Business Details
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground text-xs block">License</span>
                      <span className="font-mono">{selectedSupplier.license || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs block">UBI</span>
                      <span className="font-mono">{selectedSupplier.ubi || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="text-sm">
                      <p>
                        {selectedSupplier.locationDetails?.city}, {selectedSupplier.locationDetails?.state} {selectedSupplier.locationDetails?.zipCode}
                      </p>
                      <p className="text-muted-foreground text-xs mt-1">{selectedSupplier.locationDetails?.country}</p>
                      {selectedSupplier.locationDetails?.street && (
                        <p className="text-muted-foreground text-xs">{selectedSupplier.locationDetails.street}</p>
                      )}
                    </div>
                  </div>

                  {(selectedSupplier.email || selectedSupplier.phone) && (
                    <div className="space-y-2 pt-2 border-t">
                      {selectedSupplier.email && (
                        <div className="flex items-center gap-3 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{selectedSupplier.email}</span>
                        </div>
                      )}
                      {selectedSupplier.phone && (
                        <div className="flex items-center gap-3 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground" />
                          <span>
                            {formatPhoneDisplay(selectedSupplier.countryCode, selectedSupplier.phone)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2 pt-2 border-t text-sm">
                  <h4 className="font-medium text-muted-foreground text-xs uppercase">About</h4>
                  <p className="leading-relaxed text-muted-foreground">{selectedSupplier.description || 'No description provided.'}</p>
                </div>

                {selectedSupplier.documentLinks && selectedSupplier.documentLinks.length > 0 && (
                  <div className="space-y-2 pt-2 border-t text-sm">
                    <h4 className="font-medium text-muted-foreground text-xs uppercase flex items-center gap-1.5">
                      <FileText className="w-3 h-3" /> Documents
                    </h4>
                    <div className="grid gap-2">
                      {selectedSupplier.documentLinks.map((link: string, i: number) => (
                        <a key={i} href={link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-primary hover:underline">
                          <FileText className="w-3 h-3" />
                          <span className="truncate">Document {i + 1}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2 mt-2 border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                  <div>
                    <span className="font-medium text-foreground block mb-1">Created</span>
                    {selectedSupplier.createdAt ? formatInStoreTz(selectedSupplier.createdAt, 'MMM d, yyyy') : 'N/A'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground block mb-1">Updated</span>
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {selectedSupplier.updatedAt ? formatDistanceToNow(new Date(selectedSupplier.updatedAt), { addSuffix: true }) : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <DeleteConfirmationDialog
        open={!!supplierToDelete}
        onOpenChange={(open: boolean) => !open && setSupplierToDelete(null)}
        title="Delete Supplier?"
        description={
          <>
            This action cannot be undone. This will permanently delete the supplier{' '}
            <span className="font-medium text-foreground">"{supplierToDelete?.name}"</span> and
            remove it from our servers.
          </>
        }
        onConfirm={() => {
          if (supplierToDelete) {
            deleteSupplier.mutate(supplierToDelete.id)
            setSupplierToDelete(null)
          }
        }}
        confirmLabel="Delete Supplier"
        isPending={deleteSupplier.isPending}
      />
    </div>
  )
}
